import type { MeasurementSheetType } from '@/visualization/domain/sizing/templates/types';

/**
 * Mapping between SVG placeholders and measurement IDs
 * Used by EvarSvgMeasurementsView to replace placeholders with actual values
 */

// Type for mapping values (id and type)
interface MeasurementMapping {
    id: string;
    type: MeasurementSheetType; // Defined types
}

// Mapping of placeholders to measurements
export const SVG_PLACEHOLDER_MAPPING: Record<string, MeasurementMapping> = {
    // ── Length measurements ──
    TH_MEASURE_NECK_LENGTH: { id: 'neck-length', type: 'length' },
    TH_MEASURE_AORTA_LENGTH: { id: 'aortic-trunk-length', type: 'length' },
    TH_MEASURE_RIGHT_ILIAC_LENGTH: { id: 'right-iliac-length', type: 'length' },
    TH_MEASURE_LEFT_ILIAC_LENGTH: { id: 'left-iliac-length', type: 'length' },
    TH_MEASURE_TOTAL_RIGHT_LENGTH: { id: 'right-leg-length', type: 'length' },
    TH_MEASURE_TOTAL_LEFT_LENGTH: { id: 'left-leg-length', type: 'length' },

    // ── Diameter measurements at key points ──
    TH_MEASURE_INFRARENAL_NECK_DIAMETER: { id: 'dP2', type: 'diameter' },
    TH_MEASURE_HEALTHY_NECK_DIAMETER: { id: 'dP3', type: 'diameter' },
    TH_MEASURE_AORTA_BIFURCATION_DIAMETER: { id: 'dP4', type: 'diameter' },
    TH_MEASURE_RIGHT_ILIAC_COMMON_DIAMETER: { id: 'dP5', type: 'diameter' },
    TH_MEASURE_LEFT_ILIAC_COMMON_DIAMETER: { id: 'dP6', type: 'diameter' },

    // ── Min/Max diameters ──
    TH_MEASURE_AORTA_ANEURYSM: { id: 'max-aneurysm-d', type: 'diameter' },
    TH_MEASURE_RIGHT_ILIAC_EXTERNAL_MIN_DIAMETER: { id: 'min-right-iliac-d', type: 'diameter' },
    TH_MEASURE_LEFT_ILIAC_EXTERNAL_MIN_DIAMETER: { id: 'min-left-iliac-d', type: 'diameter' },

    // ── Angles ──
    TH_MEASURE_INFRARENAL_ANGLE: { id: 'infrarenal-angle', type: 'angle' },
    TH_MEASURE_SUPRARENAL_ANGLE: { id: 'suprarenal-angle', type: 'angle' },

    // ── Derived ──
    TH_MEASURE_INFRARENAL_NECK_CONIC_SHAPE: { id: 'neck-taper', type: 'conicity' },
};

/**
 * Get measurement ID from placeholder string
 * @param {string} placeholder - The placeholder string (e.g., 'TH_MEASURE_NECK_LENGTH')
 * @returns {MeasurementMapping | null} Mapping object with id and type, or null if not mapped
 */
export const getMeasurementFromPlaceholder = (placeholder: string): MeasurementMapping | null => {
    return SVG_PLACEHOLDER_MAPPING[placeholder] || null;
};

/**
 * Get all placeholder strings that map to a specific measurement ID
 * @param {string} measurementId - The measurement ID (e.g., 'P2-P3')
 * @returns {string[]} Array of placeholder strings
 */
export const getPlaceholdersForMeasurement = (measurementId: string): string[] => {
    return Object.entries(SVG_PLACEHOLDER_MAPPING)
        .filter(([, mapping]) => mapping?.id === measurementId)
        .map(([placeholder]) => placeholder);
};
